/**
 * UserDataService.js - Service for managing user data from dotstudioPRO API
 * Handles user-specific data operations via SpotlightService (which handles caching)
 */

/**
 * @typedef {import('./SpotlightService').ResumptionItem} ResumptionItem
 * @typedef {import('./SpotlightService').WatchlistChannel} WatchlistChannel
 * @typedef {import('./SpotlightService').ResumptionsOptions} ResumptionsOptions
 * @typedef {import('./SpotlightService').WatchPointSaveOptions} WatchPointSaveOptions
 * @typedef {import('./SpotlightService').WatchlistItem} WatchlistItem
 */

/**
 * @typedef {Object} ResumptionsData
 * @property {ResumptionItem[]} 'continue-watching' - Continue watching items
 * @property {ResumptionItem[]} 'watch-again' - Watch again items
 */

/**
 * @typedef {Object} WatchPointSaveData
 * @property {number} point - Saved watch point in seconds
 * @property {string} user_id - User identifier
 * @property {string} video_id - Video identifier
 * @property {string} company_id - Company identifier
 * @property {string} createdAt - Creation timestamp
 * @property {string} updatedAt - Last updated timestamp
 * @property {number} progress - Progress percentage (0-1)
 */

/**
 * @typedef {Object} UserData
 * @property {ResumptionsData} [resumptions] - User resumptions data
 * @property {WatchlistChannel[]} [products] - User products data
 * @property {Object} [subscription] - User subscription data
 * @property {string} [token] - User token data
 * @property {WatchlistItem[]} [watchlist] - User's watchlist items
 */

import spotlightService from './SpotlightService';
import profileService from './ProfileService';
import episodeSelectionService from './EpisodeSelectionService';

class UserDataService {
  constructor() {
    this.userData = null;
    this.watchlist = [];
    this.resumptionsData = null; // Store resumptions data separately
    this.hoverModalData = null; // Added for hover modal data
    this.resumptionMap = new Map(); // O(1) lookup map: channelId -> latest resumption
    this.categories = null; // Store categories for hover modal data refresh
  }

  /**
   * Clear all user data (for logout or profile switching)
   */
  clearCurrentUser() {
    this.userData = null;
    this.watchlist = [];
    this.resumptionsData = null;
    this.hoverModalData = null;
    this.resumptionMap.clear();
    this.categories = null;
  }

  /**
   * Initialize user data from AppLoad response (called once on app load)
   * @param {Object} appLoadUserData - User data from AppLoad response
   * @deprecated This method is no longer used. Profile-specific data is fetched separately.
   */
  initializeFromAppLoad(appLoadUserData) {
    console.warn(
      'UserDataService: initializeFromAppLoad is deprecated. Profile-specific data is now fetched separately.',
    );
    // This method is kept for backward compatibility but should not be used
  }

  /**
   * Get user's watchlist via SpotlightService (which handles caching)
   * @param {ResumptionsOptions} options - Query options
   * @returns {Promise<WatchlistChannel[]>} Array of watchlist channels
   */
  async getWatchlist(options = {}) {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        this.watchlist = [];
        return [];
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        this.watchlist = [];
        return [];
      }

      // Use SpotlightService to get watchlist data (it handles caching)
      const watchlistData = await spotlightService.getWatchlist(options);
      const channels = watchlistData.channels || [];

      // Update local state for immediate UI updates
      this.watchlist = channels;

      return channels;
    } catch (error) {
      console.error('UserDataService: Error fetching watchlist:', error);
      throw error;
    }
  }

  /**
   * Add channel to watchlist and update local state with response
   * @param {string} channelId - Channel ID to add
   * @param {string} parentChannelId - Parent channel ID (optional)
   * @returns {Promise<import('./SpotlightService').WatchlistAddResponse>} Updated watchlist response
   */
  async addToWatchlist(channelId, parentChannelId = null) {
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }

      // Use SpotlightService to add to watchlist (it handles its own cache)
      const result = await spotlightService.addToWatchlist(channelId, parentChannelId);

      // Update local state with the response data if successful
      if (result.success && result.result?.channels) {
        this.watchlist = result.result.channels;
      }

      return result;
    } catch (error) {
      console.error('UserDataService: Error adding to watchlist:', error);
      throw error;
    }
  }

  /**
   * Remove channel from watchlist and update local state with response
   * @param {string} channelId - Channel ID to remove
   * @returns {Promise<import('./SpotlightService').WatchlistDeleteResponse>} Updated watchlist response
   */
  async removeFromWatchlist(channelId) {
    try {
      if (!channelId) {
        throw new Error('Channel ID is required');
      }

      // Use SpotlightService to remove from watchlist (it handles its own cache)
      const result = await spotlightService.removeFromWatchlist(channelId);

      // Update local state with the response data if successful
      if (result.success && result.result?.channels) {
        this.watchlist = result.result.channels;
      }

      return result;
    } catch (error) {
      console.error('UserDataService: Error removing from watchlist:', error);
      throw error;
    }
  }

  /**
   * Check if a channel is in the user's watchlist
   * @param {string} channelId - The channel ID to check
   * @param {string} parentChannelId - Optional parent channel ID for series
   * @returns {boolean} Whether the channel is in the watchlist
   */
  isInWatchlist(channelId, parentChannelId = null) {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      return false;
    }

    if (!channelId) {
      return false;
    }

    // Check for exact match on the channel ID
    const isInList = this.watchlist.some(channel => channel._id === channelId);
    if (isInList) {
      return true;
    }

    // For series, also check if the parent channel is in the watchlist
    if (parentChannelId) {
      // Check top-level _id
      const isParentInList = this.watchlist.some(channel => channel._id === parentChannelId);
      if (isParentInList) {
        return true;
      }

      // Check nested parent_channel._id (for season items in watchlist)
      const isParentNestedInList = this.watchlist.some(
        channel => channel.parent_channel?._id === parentChannelId,
      );
      if (isParentNestedInList) {
        return true;
      }
    }

    // Check if this channelId is a parent and any of its children are in the watchlist
    // This handles the case where we're checking a series but the watchlist contains its seasons
    const childInWatchlist = this.watchlist.some(channel => {
      // Check if this watchlist item has a parent_channel that matches our channelId
      return channel.parent_channel?._id === channelId;
    });
    if (childInWatchlist) {
      return true;
    }

    return false;
  }

  /**
   * Get the number of items in the watchlist
   * @returns {number} Number of items in the watchlist
   */
  getWatchlistCount() {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      return 0;
    }

    return this.watchlist.length;
  }

  /**
   * Get continue watching data from stored resumptions
   * @returns {Array} Array of continue watching items
   */
  getContinueWatching() {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      return [];
    }

    const data = this.resumptionsData?.['continue-watching'] || [];
    return data;
  }

  /**
   * Get watch again data from stored resumptions (completed content)
   * @returns {Array} Array of watch again items
   */
  getWatchAgain() {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      return [];
    }

    return this.resumptionsData?.['watch-again'] || [];
  }

  /**
   * Get continue watching data directly from API
   * @returns {Promise<Array>} Array of continue watching items
   */
  async getContinueWatchingData() {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        return [];
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        return [];
      }

      const resumptionsData = await spotlightService.getResumptions();
      return resumptionsData.data?.['continue-watching'] || [];
    } catch (error) {
      console.error('UserDataService: Error fetching continue watching data:', error);
      return [];
    }
  }

  /**
   * Get watch again data directly from API
   * @returns {Promise<Array>} Array of watch again items
   */
  async getWatchAgainData() {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        return [];
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        return [];
      }

      const resumptionsData = await spotlightService.getResumptions();
      return resumptionsData.data?.['watch-again'] || [];
    } catch (error) {
      console.error('UserDataService: Error fetching watch again data:', error);
      return [];
    }
  }

  /**
   * Get all resumptions data (continue watching and watch again) directly from API
   * @param {ResumptionsOptions} options - Query options for the resumptions API
   * @returns {Promise<ResumptionsData>} Object with continue-watching and watch-again arrays
   */
  async getAllResumptionsData(options = {}) {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        return {
          'continue-watching': [],
          'watch-again': [],
        };
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        return {
          'continue-watching': [],
          'watch-again': [],
        };
      }

      const resumptionsData = await spotlightService.getResumptions(options);
      return {
        'continue-watching': resumptionsData.data?.['continue-watching'] || [],
        'watch-again': resumptionsData.data?.['watch-again'] || [],
      };
    } catch (error) {
      console.error('UserDataService: Error fetching all resumptions data:', error);
      return {
        'continue-watching': [],
        'watch-again': [],
      };
    }
  }

  /**
   * Get user subscription products
   * @returns {Object} Object containing svod and tvod products
   */
  getUserProducts() {
    return this.userData?.products || { svod: [], tvod: [] };
  }

  /**
   * Get user subscription info
   * @returns {Object} Subscription information
   */
  getUserSubscription() {
    return this.userData?.subscription || null;
  }

  /**
   * Get user token from user data
   * @returns {string} User token
   */
  getUserToken() {
    return this.userData?.token || null;
  }

  /**
   * Get watch point for a specific video
   * @param {string} videoId - The ID of the video to get watch point for
   * @returns {Promise<Object|null>} Full watch point response data, or null if no data exists
   */
  async getVideoWatchPoint(videoId) {
    try {
      const response = await spotlightService.getVideoWatchPoint(videoId);
      return response.data || null;
    } catch (error) {
      console.error('UserDataService: Error fetching video watch point:', error);
      return null;
    }
  }

  /**
   * Save watch point for a specific video
   * @param {string} videoId - The ID of the video to save watch point for
   * @param {number} point - Time in seconds the user has watched the video to
   * @param {WatchPointSaveOptions} options - Optional parameters
   * @returns {Promise<WatchPointSaveData>} Response with saved point data including progress percentage
   */
  async saveVideoWatchPoint(videoId, point, options = {}) {
    try {
      const response = await spotlightService.saveVideoWatchPoint(videoId, point, options);

      // Rebuild resumption map after saving watch point
      this.buildResumptionMap();

      return response.data;
    } catch (error) {
      console.error('UserDataService: Error saving video watch point:', error);
      throw error;
    }
  }

  /**
   * Find the latest watched episode for a series by analyzing continue-watching resumptions
   * @param {string} seriesId - The parent series ID to find the latest episode for
   * @returns {Object|null} Object with episode info and watch point, or null if no watched episodes found
   */
  findLatestWatchedEpisode(seriesId) {
    try {
      // Get continue-watching data from stored resumptions
      const continueWatching = this.getContinueWatching();

      if (!continueWatching || continueWatching.length === 0) {
        return null;
      }

      // Filter for episodes that belong to this series
      const seriesEpisodes = continueWatching.filter(item => {
        const matchesParentChannel =
          item.parent_channel?._id === seriesId || item.parent_channel?.channel_id === seriesId;
        const matchesChannel =
          item.channel?._id === seriesId || item.channel?.channel_id === seriesId;
        return matchesParentChannel || matchesChannel;
      });

      if (seriesEpisodes.length === 0) {
        return null;
      }

      // Sort by updatedAt timestamp (most recent first)
      seriesEpisodes.sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA; // Most recent first
      });

      const latestEpisode = seriesEpisodes[0];

      return {
        episode: {
          _id: latestEpisode._id,
          title: latestEpisode.title,
          duration: latestEpisode.duration,
          episode: latestEpisode.episode,
          channel_id: latestEpisode.channel?._id,
          channel: latestEpisode.channel,
          parent_channel: latestEpisode.parent_channel,
        },
        watchPoint: latestEpisode.point,
        progress: latestEpisode.point / (latestEpisode.duration || 1),
        updatedAt: latestEpisode.updatedAt,
        totalWatchedEpisodes: seriesEpisodes.length,
      };
    } catch (error) {
      console.error('UserDataService: Error finding latest watched episode:', error);
      return null;
    }
  }

  /**
   * Get watch progress for all episodes in a series
   * @param {string} seriesId - The parent series ID
   * @returns {Object} Object mapping episode IDs to progress data
   */
  getSeriesEpisodeProgress(seriesId) {
    try {
      // Get both continue-watching and watch-again data from stored resumptions
      const continueWatching = this.getContinueWatching();
      const watchAgain = this.getWatchAgain();

      // Combine both arrays to get all episode progress data
      const allEpisodeData = [...(continueWatching || []), ...(watchAgain || [])];

      if (!allEpisodeData || allEpisodeData.length === 0) {
        return {};
      }

      // Filter for episodes that belong to this series
      const seriesEpisodes = allEpisodeData.filter(item => {
        const matchesParentChannel =
          item.parent_channel?._id === seriesId || item.parent_channel?.channel_id === seriesId;
        const matchesChannel =
          item.channel?._id === seriesId || item.channel?.channel_id === seriesId;
        return matchesParentChannel || matchesChannel;
      });

      // Create progress mapping
      const progressMap = {};
      seriesEpisodes.forEach(item => {
        const progress = item.point / (item.duration || 1);
        if (progress > 0) {
          // Only include episodes with actual progress
          progressMap[item._id] = {
            point: item.point,
            duration: item.duration,
            progress: progress,
            updatedAt: item.updatedAt,
            // Add flag to indicate if this is from watch-again (completed) or continue-watching (in-progress)
            isCompleted: watchAgain.some(watchAgainItem => watchAgainItem._id === item._id),
          };
        }
      });

      return progressMap;
    } catch (error) {
      console.error('UserDataService: Error getting series episode progress:', error);
      return {};
    }
  }

  /**
   * Refresh resumptions data from API and update stored data
   * This should be called when user leaves a watch page to ensure
   * continue-watching data is up to date
   */
  async refreshResumptionsData() {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        this.resumptionsData = null;
        this.resumptionMap.clear();
        return null;
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        this.resumptionsData = null;
        this.resumptionMap.clear();
        return null;
      }

      // Get fresh resumptions data from API
      const resumptionsData = await spotlightService.getResumptions();

      // Update the stored resumptions data
      if (resumptionsData.data) {
        this.resumptionsData = resumptionsData.data;

        // Rebuild resumption map after refreshing data
        this.buildResumptionMap();

        // Rebuild hover modal data with fresh resumption data if categories are available
        // This ensures progress bars show updated data
        if (this.categories && this.categories.length > 0) {
          await this.preCalculateHoverModalData(this.categories);
        } else {
          // Clear hover modal data so it gets rebuilt when categories become available
          this.hoverModalData = null;
        }
      }

      return resumptionsData.data;
    } catch (error) {
      console.error('UserDataService: Error refreshing resumptions data:', error);
      return null;
    }
  }

  /**
   * Refresh watchlist data from API and update stored data
   * @returns {Promise<WatchlistChannel[]>} Array of watchlist channels
   */
  async refreshWatchlistData() {
    try {
      // Check if we have a current profile
      const currentProfile = profileService.getCurrentProfile();
      if (!currentProfile?._id) {
        this.watchlist = [];
        return [];
      }

      // Check if we have a user (short-circuit if not authenticated)
      const user = spotlightService.getCurrentUser();
      if (!user || !user.email) {
        this.watchlist = [];
        return [];
      }

      // Fetch fresh watchlist data from API
      const freshWatchlistData = await this.getWatchlist();

      return freshWatchlistData;
    } catch (error) {
      console.error('UserDataService: Error refreshing watchlist data:', error);
      return [];
    }
  }

  /**
   * Pre-calculate hover modal data for all channels using AppLoad data
   * This eliminates the need for the hover modal to fetch series details or recalculate data
   * @param {Array} categories - Categories array from AppLoad
   * @returns {Object} Map of channel ID to hover modal data
   */
  async preCalculateHoverModalData(categories) {
    if (!categories || !Array.isArray(categories)) {
      return {};
    }

    const hoverModalData = {};

    // Get enhanced continue watching data that includes "next episode" items
    const enhancedContinueWatching = await this.getEnhancedContinueWatchingData();

    // Process each category and its channels
    categories.forEach(category => {
      if (!category.channels || !Array.isArray(category.channels)) return;

      category.channels.forEach(channel => {
        if (!channel._id) return;

        const channelId = channel._id;

        // More comprehensive content type detection
        const hasChildChannels = channel.childchannels && channel.childchannels.length > 0;
        const isProgrammingTypeSeries = channel.programming_type === 'Series';
        const isTypeSeries = channel.type === 'series';
        const isContentTypeSeries = channel.content_type === 'series';
        const isProgrammingTypeMovie = channel.programming_type === 'Movie';
        const isTypeMovie = channel.type === 'movie';
        const isContentTypeMovie = channel.content_type === 'movie';

        // Determine if it's a series or movie
        const isSeries =
          hasChildChannels || isProgrammingTypeSeries || isTypeSeries || isContentTypeSeries;
        const isMovie =
          isProgrammingTypeMovie ||
          isTypeMovie ||
          isContentTypeMovie ||
          (!isSeries && !hasChildChannels);

        // Find resumption data for this channel
        let resumptionData = null;
        let videoId = null;
        let startTime = 0;

        // First, try to find this channel in the enhanced continue watching data
        const enhancedItem = enhancedContinueWatching.find(item => {
          const itemId = item.parent_channel?._id || item.channel?._id || item._id;
          return itemId === channelId;
        });
        if (channel.slug.includes('dick-tracy') || channel.slug.includes('vanish'))
          if (enhancedItem) {
            // Use the enhanced data which includes "next episode" items
            resumptionData = {
              progress: enhancedItem.progress || 0,
              point: enhancedItem.point || 0,
              duration: enhancedItem.duration || 0,
              title: enhancedItem.title, // This should be the episode title for "next episode" items
              episodeData: enhancedItem,
              isSeries: isSeries,
              seasonNumber: enhancedItem.season || enhancedItem.channel?.season_number || 1,
              episodeNumber: enhancedItem.episode || enhancedItem.channel?.episode_number || 1,
            };
            startTime = enhancedItem.point || 0;
            videoId = enhancedItem.video_id || enhancedItem._id;
          } else if (enhancedContinueWatching?.length > 0) {
            // For series, use enhanced episode selection logic
            if (isSeries && channel.childchannels && channel.childchannels.length > 0) {
              const continueWatchingInfo = episodeSelectionService.getContinueWatchingEpisode(
                channelId,
                channel.childchannels,
              );

              if (continueWatchingInfo) {
                const episode = continueWatchingInfo.episode;

                // Find the resumption data for this episode
                const episodeResumption = enhancedContinueWatching.find(
                  item =>
                    item._id === episode._id ||
                    item.video_id === episode._id ||
                    item.channel?._id === episode._id,
                );

                if (episodeResumption) {
                  resumptionData = {
                    progress: episodeResumption.progress,
                    point: episodeResumption.point,
                    duration: episodeResumption.duration,
                    title: episodeResumption.title,
                    episodeData: episodeResumption,
                    isSeries: isSeries,
                    seasonNumber: continueWatchingInfo.season?.season_number || 1,
                    episodeNumber: episode.episode_number || episode.order || 1,
                  };
                  startTime = episodeResumption.point || 0;
                } else {
                  // No resumption data for this episode, but it's the next episode to watch
                  resumptionData = {
                    progress: 0,
                    point: 0,
                    duration: episode.video?.duration || episode.duration || 0,
                    title: episode.title,
                    episodeData: episode,
                    isSeries: isSeries,
                    seasonNumber: continueWatchingInfo.season?.season_number || 1,
                    episodeNumber: episode.episode_number || episode.order || 1,
                  };
                  startTime = 0;
                }

                videoId = episode._id;
              }
            } else {
              // For movies or non-series content, use existing logic
              const channelEpisodes = enhancedContinueWatching.filter(item => {
                // Match by channel ID (for individual episodes)
                if (item.channel?._id === channelId) {
                  return true;
                }
                // Match by parent channel ID (for series episodes)
                if (item.parent_channel?._id === channelId) {
                  return true;
                }
                // Match by channel slug
                if (
                  item.channel?.slug === channel.slug ||
                  item.parent_channel?.slug === channel.slug
                ) {
                  return true;
                }
                return false;
              });

              if (channelEpisodes.length > 0) {
                // Sort by updatedAt timestamp to get the most recently watched episode
                channelEpisodes.sort((a, b) => {
                  const dateA = new Date(a.updatedAt || 0);
                  const dateB = new Date(b.updatedAt || 0);
                  return dateB - dateA; // Most recent first
                });

                const latestEpisode = channelEpisodes[0];
                resumptionData = {
                  progress: latestEpisode.progress,
                  point: latestEpisode.point,
                  duration: latestEpisode.duration,
                  title: latestEpisode.title,
                  episodeData: latestEpisode,
                  isSeries: isSeries,
                  // Extract season and episode numbers for ProgressBar component
                  seasonNumber: latestEpisode.channel?.season || 1,
                  episodeNumber: latestEpisode.episode || 1,
                };
                // Use the resumption point as start time
                startTime = latestEpisode.point || 0;
                videoId = latestEpisode._id;
              }
            }
          }

        // If no resumption data, calculate fallback start time and video ID
        if (!resumptionData) {
          if (isMovie) {
            // For movies, use channel's video data
            videoId = channel.video_id || channel.video?._id;

            // Try multiple sources for duration data
            let duration = channel.video?.duration || channel.duration || 0;
            if (duration) {
              startTime = Math.floor(Number(duration) * 0.2); // 20% fallback
            }
          } else if (isSeries && channel.childchannels && channel.childchannels.length > 0) {
            // For series, use the first episode of the first season
            const firstSeason = channel.childchannels[0];
            if (firstSeason && firstSeason.playlist && firstSeason.playlist.length > 0) {
              const firstEpisode = firstSeason.playlist[0];
              videoId = firstEpisode._id;

              // Try multiple sources for duration data
              let duration = firstEpisode.video?.duration || firstEpisode.duration || 0;
              if (duration) {
                startTime = Math.floor(Number(duration) * 0.2); // 20% fallback
              } else {
                console.warn('UserDataService: No duration data available for series episode:', {
                  channelTitle: channel.title,
                  episodeTitle: firstEpisode.title,
                  videoId,
                  hasVideoData: !!firstEpisode.video,
                  hasDuration: !!firstEpisode.duration,
                });
              }
            } else {
              // No episodes available for series - this is normal for some series
            }
          } else if (isSeries) {
            // For series detected in AppLoad but without childchannels, we can't pre-calculate
            // The hover modal will need to fetch the full series details to get video ID
          }
        }

        // Store the pre-calculated data
        hoverModalData[channelId] = {
          videoId,
          startTime,
          resumptionData,
          isMovie,
          isSeries,
          channel,
        };
      });
    });

    this.hoverModalData = hoverModalData;
    this.categories = categories; // Store categories for future refresh
    return hoverModalData;
  }

  /**
   * Get pre-calculated hover modal data for a specific channel
   * @param {string} channelId - The channel ID to get data for
   * @returns {Object|null} Pre-calculated hover modal data or null if not found
   */
  getHoverModalData(channelId) {
    if (!this.hoverModalData) {
      console.warn(
        'UserDataService: Hover modal data not initialized, call preCalculateHoverModalData first',
      );
      return null;
    }
    return this.hoverModalData[channelId] || null;
  }

  /**
   * Get enhanced continue watching data that includes series with completed episodes
   * This method adds series from watch-again data to the continue-watching data
   * so that series with completed episodes appear in the Continue Watching rail
   * @returns {Promise<Array>} Enhanced continue watching data
   */
  async getEnhancedContinueWatchingData() {
    const continueWatching = this.getContinueWatching();
    const watchAgain = this.getWatchAgain();

    if ((!continueWatching || !continueWatching.length) && (!watchAgain || !watchAgain.length)) {
      return [];
    }

    // Find series that have completed episodes but no continue-watching episodes
    const resumptionsData = new Map();

    const resumptionItems = watchAgain
      .map(item => {
        const channelId =
          item.parent_channel?._id ||
          (item.channel?.programming_type === 'Series' ? item.channel._id : null);
        if (!channelId) return false;

        return {
          channelId,
          seriesEpisodeData: item,
          updatedAt: item.updatedAt,
        };
      })
      .filter(Boolean);

    // Group watch-again items by series
    continueWatching.forEach(item => {
      const channelId = item.parent_channel?._id || item.channel._id;

      if (channelId) {
        resumptionItems.push({
          channelId,
          continueWatchingData: item,
          updatedAt: item.updatedAt,
        });
      }
    });

    resumptionItems
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA; // Most recent first
      })
      .forEach(item => {
        if (!resumptionsData.has(item.channelId)) {
          resumptionsData.set(item.channelId, item);
        }
      });

    // Create mock continue-watching items for series with completed episodes
    const enhancedItems = [];

    for (const [channelId, channelData] of resumptionsData) {
      // Only add if this series doesn't already have continue-watching episodes
      // Try to get the actual next episode info using EpisodeSelectionService
      let nextEpisodeTitle = 'Next Episode';
      let nextEpisodeInfo = null;

      try {
        if (channelData.seriesEpisodeData) {
          // Get series details to pass seasons to getContinueWatchingEpisode
          const seriesDetails = await spotlightService.getChannelById(channelId);
          const seasons = seriesDetails?.childchannels || [];

          nextEpisodeInfo = episodeSelectionService.getContinueWatchingEpisode(channelId, seasons);
          if (nextEpisodeInfo && nextEpisodeInfo.episode) {
            nextEpisodeTitle = nextEpisodeInfo.episode.title || 'Next Episode';
          }
        } else if (channelData.continueWatchingData?.title) {
          nextEpisodeTitle = channelData.continueWatchingData?.title;
        }
      } catch (error) {
        console.error('Error getting next episode info for series:', channelId, error);
      }

      const currentInfo = channelData.seriesEpisodeData || channelData.continueWatchingData;
      const seriesTitle = currentInfo?.parent_channel?.title;
      const programming_type = currentInfo?.channel?.programming_type;
      // Create a mock continue-watching item for the next episode
      const mockItem = {
        _id: channelId,
        title: nextEpisodeTitle,
        seriestitle: seriesTitle,
        channel: nextEpisodeInfo?.season || currentInfo?.channel,
        episode_title: nextEpisodeTitle,
        channel_type: programming_type === 'Series' ? 'series' : 'movie',
        programming_type: programming_type === 'Series' ? 'Series' : 'Movie',
        progress: nextEpisodeInfo ? 0 : currentInfo.progress,
        point: nextEpisodeInfo ? 0 : currentInfo.point,
        duration: nextEpisodeInfo?.episode?.duration || currentInfo.duration || 0,
        updatedAt: currentInfo.updatedAt, // Use completion time
        // Copy additional metadata
        description: nextEpisodeInfo?.episode?.description || currentInfo.description || '',
        slug: nextEpisodeInfo?.episode?.slug || '',
        spotlight_poster:
          nextEpisodeInfo?.episode?.spotlight_poster || currentInfo.spotlight_poster || '',
        poster: nextEpisodeInfo?.episode?.poster || currentInfo.poster || '',
        thumb: nextEpisodeInfo?.episode?.thumb || currentInfo.thumb || '',
        video_id: nextEpisodeInfo?.episode?._id || currentInfo._id,
        rating: nextEpisodeInfo?.episode?.rating || currentInfo.rating || '',
        // Add resumption_data for progress bar display
        resumption_data: {
          progress: nextEpisodeInfo ? 0 : currentInfo.progress,
          point: nextEpisodeInfo ? 0 : currentInfo.point,
          duration: nextEpisodeInfo?.episode?.duration || currentInfo.duration || 0,
        },
      };

      if (programming_type === 'Series') {
        mockItem.episode =
          nextEpisodeInfo?.episode?.episode_number || nextEpisodeInfo?.episode?.episode || 1;
        mockItem.season =
          nextEpisodeInfo?.season?.season_number || nextEpisodeInfo?.season?.season || 1;
        mockItem.resumption_data.episodeNumber = mockItem.episode;
        mockItem.resumption_data.seasonNumber = mockItem.season;
      }

      if (currentInfo?.parent_channel) {
        mockItem.parent_channel = {
          _id: channelId,
          title: currentInfo.parent_channel?.title,
          programming_type: 'Series',
          description: currentInfo.parent_channel?.description || '',
          slug: currentInfo.parent_channel?.slug || '',
          spotlight_poster: currentInfo.parent_channel?.spotlight_poster || '',
          poster: currentInfo.parent_channel?.poster || '',
          thumb: currentInfo.thumb || '',
        };
      }

      enhancedItems.push(mockItem);
    }

    return enhancedItems;
  }

  /**
   * Get the latest episode for each series from continue watching data
   * This consolidates the logic used in preCalculateHoverModalData and continue watching rail
   * @param {Array} continueWatchingData - Array of continue watching items
   * @returns {Array} Array with only the latest episode per series
   */
  getLatestEpisodesPerSeries(continueWatchingData) {
    if (!continueWatchingData || continueWatchingData.length === 0) {
      return [];
    }

    // Group episodes by series (parent_channel or channel)
    const seriesGroups = new Map();

    continueWatchingData.forEach(item => {
      let seriesId = null;
      let seriesTitle = null;

      // Special handling for next episodes - treat them as separate items
      if (item._id && item._id.startsWith('next-episode-')) {
        // For next episodes, use the unique ID as the series ID to avoid grouping
        seriesId = item._id;
        seriesTitle = item.seriestitle || item.title;
      } else if (item.parent_channel) {
        // Episode belongs to a series
        seriesId = item.parent_channel._id;
        seriesTitle = item.parent_channel.title;
      } else if (
        item.channel &&
        (item.channel.programming_type === 'Series' || item.channel.content_type === 'series')
      ) {
        // Channel itself is a series
        seriesId = item.channel._id;
        seriesTitle = item.channel.title;
      } else {
        // Individual movie or episode, use its own ID
        seriesId = item._id;
        seriesTitle = item.title;
      }

      if (!seriesGroups.has(seriesId)) {
        seriesGroups.set(seriesId, []);
      }
      seriesGroups.get(seriesId).push(item);
    });

    // For each series, find the latest episode by updatedAt timestamp
    // But prioritize "next episode" items over regular continue watching data
    const latestEpisodes = [];
    seriesGroups.forEach((episodes, seriesId) => {
      if (episodes.length === 1) {
        // Only one episode for this series, use it directly
        latestEpisodes.push(episodes[0]);
      } else {
        // Multiple episodes - prioritize "next episode" items
        const nextEpisodeItems = episodes.filter(item => item.isNextEpisode);
        const regularItems = episodes.filter(item => !item.isNextEpisode);

        if (nextEpisodeItems.length > 0) {
          // If we have "next episode" items, use the most recent one
          nextEpisodeItems.sort((a, b) => {
            const dateA = new Date(a.updatedAt || 0);
            const dateB = new Date(b.updatedAt || 0);
            return dateB - dateA; // Most recent first
          });
          latestEpisodes.push(nextEpisodeItems[0]);
        } else {
          // No "next episode" items, use the most recent regular item
          regularItems.sort((a, b) => {
            const dateA = new Date(a.updatedAt || 0);
            const dateB = new Date(b.updatedAt || 0);
            return dateB - dateA; // Most recent first
          });
          latestEpisodes.push(regularItems[0]);
        }
      }
    });

    // Sort all episodes by updatedAt timestamp (most recent first)
    latestEpisodes.sort((a, b) => {
      const dateA = new Date(a.updatedAt || 0);
      const dateB = new Date(b.updatedAt || 0);
      return dateB - dateA; // Most recent first
    });

    return latestEpisodes;
  }

  /**
   * Build the resumption map for O(1) lookup
   * This pre-calculates the latest resumption for each channel/series
   */
  buildResumptionMap() {
    this.resumptionMap.clear();

    const continueWatching = this.getContinueWatching();
    const watchAgain = this.getWatchAgain();

    // Process all resumptions and find the latest for each channel
    const initialResumptions = [];

    // Add continue-watching items
    continueWatching.forEach(item => {
      initialResumptions.push({
        ...item,
        type: 'continue-watching',
        episode: {
          _id: item._id,
          title: item.title,
          duration: item.duration,
          episode: item.episode,
          channel_id: item.channel?._id,
          channel: item.channel,
          parent_channel: item.parent_channel,
        },
      });
    });

    // Add watch-again items
    watchAgain.forEach(item => {
      initialResumptions.push({
        ...item,
        type: 'watch-again',
        episode: {
          _id: item._id,
          title: item.title,
          duration: item.duration,
          episode: item.episode,
          channel_id: item.channel?._id,
          channel: item.channel,
          parent_channel: item.parent_channel,
        },
      });
    });

    initialResumptions
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA; // Most recent first
      })
      .forEach(resumption => {
        // For series, use parent_channel ID; for movies, use channel ID
        const channelId = resumption.parent_channel?._id || resumption.channel?._id;
        if (!channelId || this.resumptionMap.has(channelId)) return;

        const progress = resumption.duration > 0 ? resumption.point / resumption.duration : 0;
        this.resumptionMap.set(channelId, {
          ...resumption,
          progress,
        });
      });
  }

  /**
   * Get the latest resumption for a specific channel/series (O(1) lookup)
   * @param {string} channelId - The channel or series ID
   * @returns {Object|null} Latest resumption data or null if not found
   */
  getLatestResumption(channelId) {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      return null;
    }

    if (!channelId) {
      console.warn('UserDataService: getLatestResumption called with null/undefined channelId');
      return null;
    }

    return this.resumptionMap.get(channelId) || null;
  }

  /**
   * Get all resumption data for a specific series (for episode selection)
   * @param {string} seriesId - The series ID
   * @returns {Object|null} Object with continue-watching and watch-again arrays
   */
  getResumptionDataForSeries(seriesId) {
    // Check if we have a current profile
    const currentProfile = profileService.getCurrentProfile();
    if (!currentProfile?._id) {
      // No profile selected, returning null for series resumption data
      return null;
    }

    // Use the pre-calculated resumption map for O(1) lookup
    const latestResumption = this.getLatestResumption(seriesId);

    if (!latestResumption) {
      return null;
    }

    // For backward compatibility, return the structure expected by EpisodeSelectionService
    // Since we only need the latest resumption for episode selection, we can optimize this
    const continueWatching =
      latestResumption.type === 'continue-watching' ? [latestResumption] : [];
    const watchAgain = latestResumption.type === 'watch-again' ? [latestResumption] : [];

    return {
      'continue-watching': continueWatching,
      'watch-again': watchAgain,
    };
  }
}

// Export singleton instance
export const userDataService = new UserDataService();
export default userDataService;
